import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import type { ActiveAgentRun } from "../api/client.ts";
import { humanize } from "../utils/format.ts";
import LiveDot, { LIVE_DOT_BOX } from "./LiveDot.tsx";

function agentLabel(run: ActiveAgentRun): string {
  return run.agent_name || humanize(run.agent_type);
}

/** "Contact research is running" / "2 agents are working on this: A, B". */
export function agentRunsTitle(runs: ActiveAgentRun[]): string {
  if (runs.length === 1) {
    const run = runs[0];
    return `${agentLabel(run)} is ${run.status === "running" ? "running" : "starting"}`;
  }
  return `${runs.length} agents are working on this: ${runs.map(agentLabel).join(", ")}`;
}

/**
 * Marks something an agent is working on right now with the live dot; who is
 * working is in its tooltip, or beside it with `showLabel` (a page header).
 * Nothing when no agent is.
 */
export default function AgentActivity({
  runs,
  showLabel = false,
}: {
  runs: ActiveAgentRun[] | null | undefined;
  showLabel?: boolean;
}) {
  if (!runs?.length) return null;
  const title = agentRunsTitle(runs);
  if (showLabel) {
    return (
      <Box component="span" role="status" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        <Box component="span" sx={LIVE_DOT_BOX}>
          <LiveDot />
        </Box>
        {title}
      </Box>
    );
  }
  return (
    <Tooltip title={title}>
      <Box component="span" role="status" aria-label={title} sx={LIVE_DOT_BOX}>
        <LiveDot />
      </Box>
    </Tooltip>
  );
}
